import type { MessageTemplate, ReplacementEvent } from '../../../../lib/shared/types';
import { translations, type Language } from '../../i18n/translations';

export const messageVariables = ['firstname', 'assignment', 'date'] as const;
const tokenPattern = /\{\{(firstname|assignment|date)\}\}/g;

export function validMessageTemplate(value: MessageTemplate): boolean {
  const remainder = value.text.replace(tokenPattern, '');
  return (
    ['en', 'de'].includes(value.language) &&
    value.text.trim().length > 0 &&
    Array.from(value.text).length <= 2000 &&
    new TextEncoder().encode(value.text).length <= 8000 &&
    !value.text.includes('{{{') &&
    !value.text.includes('}}}') &&
    !remainder.includes('{{') &&
    !remainder.includes('}}')
  );
}

export function defaultMessageTemplate(
  language: Language,
  name: string,
  isUnassigned = false
): MessageTemplate {
  return {
    language,
    text: translations[language][
      isUnassigned ? 'assignment.messageDefault' : 'message.default'
    ].replace('{{sender}}', () => name),
  };
}

export function messageAssignment(event: ReplacementEvent, language: Language): string {
  const t = (key: string) => translations[language][key] || translations.en[key];
  let label: string;
  if (event.serviceRole) {
    label =
      t(`publisherForm.service.${event.serviceRole}`) ||
      event.title ||
      t('replacement.domain.service');
    if (event.slotId && event.slotId !== 'main') label += ` ${event.slotId.replace('Mic', '')}`;
  } else if (event.itemType === 'prayer' && event.slot) {
    label = t(`midweekPlanning.releasedPrayer.${event.slot}`);
  } else if (event.itemType === 'chairman') {
    label = t('midweekPlanning.itemType.chairman');
  } else {
    label = event.title || t(`replacement.domain.${event.domain}`);
  }
  return `${label}${event.responsibility === 'assistant' ? ` (${t('midweekPlanning.releasedPartner')})` : ''}`;
}

export function renderMessage(
  template: MessageTemplate,
  firstName: string,
  target: ReplacementEvent,
  currentYear = new Date().getFullYear()
): string | null {
  if (!validMessageTemplate(template) || !firstName.trim()) return null;
  const date = new Date(`${target.date}T00:00:00Z`);
  if (
    !/^\d{4}-\d{2}-\d{2}$/.test(target.date) ||
    !Number.isFinite(date.getTime()) ||
    date.toISOString().slice(0, 10) !== target.date
  )
    return null;
  const values = {
    firstname: firstName,
    assignment: messageAssignment(target, template.language),
    date: new Intl.DateTimeFormat(template.language === 'de' ? 'de-DE' : 'en-GB', {
      day: 'numeric',
      month: 'long',
      timeZone: 'UTC',
      ...(date.getUTCFullYear() !== currentYear ? { year: 'numeric' as const } : {}),
    }).format(date),
  };
  return template.text.replace(tokenPattern, (_match, key: keyof typeof values) => values[key]);
}
